/**
 * Account Creation Utilities
 * 
 * Handles initialization of default settings when new users sign up
 */

import { createClient } from '@blinkdotnew/sdk';

const blink = createClient({
  projectId: 'lemon-ui-agentic-email-ai-agent-ben3ou87',
  authRequired: false
});

/**
 * Initialize default sync settings for a new user
 * This should be called after user account is created
 */
export async function initializeUserSyncDefaults(userId: string): Promise<void> {
  try {
    // Check if user already has sync settings
    const existing = await blink.db.userSyncSettings.list({
      where: { userId },
      limit: 1,
    });

    if (existing && existing.length > 0) {
      console.log('User already has sync settings, skipping initialization');
      return;
    }

    // Create default sync settings for new user
    await blink.db.userSyncSettings.create({
      id: `sync_${userId}_${Date.now()}`,
      userId,
      maxRetries: 3,
      initialDelay: 2000,
      maxDelay: 10000,
      backoffMultiplier: 2.0,
    });

    console.log(`✅ Initialized default sync settings for user ${userId}`);
  } catch (error) {
    console.error('Failed to initialize user sync defaults:', error);
    // Don't throw - this is not critical for user signup
  }
}

/**
 * Initialize all default settings for a new user
 * Includes sync settings, AI settings, notification settings, etc.
 */
export async function initializeNewUserDefaults(userId: string, userEmail: string): Promise<void> {
  try {
    console.log(`Initializing defaults for new user: ${userId}`);

    // Initialize sync settings
    await initializeUserSyncDefaults(userId);

    // Initialize AI settings
    const existingAI = await blink.db.aiSettings.list({
      where: { userId },
      limit: 1,
    });

    if (!existingAI || existingAI.length === 0) {
      await blink.db.aiSettings.create({
        id: `ai_${userId}_${Date.now()}`,
        userId,
        provider: 'blink',
        model: 'auto',
        responseSpeed: 3,
        smartCategorization: "1",
        autoReplySuggestions: "0",
        sentimentAnalysis: "1",
        allowAiLearning: "1",
      });
      console.log('✅ Initialized AI settings');
    }

    // Initialize notification settings
    const existingNotif = await blink.db.notificationSettings.list({
      where: { userId },
      limit: 1,
    });

    if (!existingNotif || existingNotif.length === 0) {
      await blink.db.notificationSettings.create({
        id: `notif_${userId}_${Date.now()}`,
        userId,
        emailNotifications: "1",
        desktopNotifications: "1",
        soundAlerts: "0",
        highPriorityEmails: "1",
        vipContacts: "1",
        meetingInvitations: "1",
        taskDeadlines: "1",
        workflowCompletions: "1",
        systemUpdates: "1",
      });
      console.log('✅ Initialized notification settings');
    }

    // Initialize general user settings
    const existingGeneral = await blink.db.userSettings.list({
      where: { userId },
      limit: 1,
    });

    if (!existingGeneral || existingGeneral.length === 0) {
      await blink.db.userSettings.create({
        id: `settings_${userId}_${Date.now()}`,
        userId,
        displayName: userEmail.split('@')[0], // Use email prefix as initial display name
        timezone: 'UTC',
        theme: 'default',
        compactMode: "0",
        language: 'en',
      });
      console.log('✅ Initialized general settings');
    }

    // Initialize security settings
    const existingSecurity = await blink.db.securitySettings.list({
      where: { userId },
      limit: 1,
    });

    if (!existingSecurity || existingSecurity.length === 0) {
      await blink.db.securitySettings.create({
        id: `security_${userId}_${Date.now()}`,
        userId,
        twoFactorEnabled: "0",
        lastPasswordChange: new Date().toISOString(),
      });
      console.log('✅ Initialized security settings');
    }

    console.log(`✅ All defaults initialized for user ${userId}`);
  } catch (error) {
    console.error('Error initializing user defaults:', error);
    // Don't throw - allow user to continue even if defaults fail
  }
}
